export class Dot {
  constructor(halfx, halfy, options = {}) {
    this.halfx = halfx;
    this.halfy = halfy;
    
    const cryptoRandom = () => crypto.getRandomValues(new Uint32Array(1))[0] / 4294967295;
    
    this.x = options.x ?? cryptoRandom() * halfx * 2;
    this.y = options.y ?? cryptoRandom() * halfy * 1.6;
    this.dx = options.dx ?? (cryptoRandom() - 0.5) * 2;
    this.dy = options.dy ?? (cryptoRandom() - 0.5) * 0.5;
    this.speed = options.speed ?? 1;
    this.size = options.size ?? cryptoRandom() * 5 + 1;
    this.color = options.color ?? Math.floor(cryptoRandom() * 256);
    this.label = options.label ?? null;
    this.kind = options.kind ?? 'star';
  }

  getPosition() {
    return { x: this.x, y: this.y };
  }

  move(dtSeconds = 1 / 60) {
    // Preserve legacy speeds tuned for ~60fps by scaling with a 60 factor
    const timeScaledSpeed = this.speed * (dtSeconds * 60);
    this.x += this.dx * timeScaledSpeed;
    this.y += this.dy * timeScaledSpeed;
    
    if (this.x < 0) this.x = this.halfx * 2;
    if (this.x > this.halfx * 2) this.x = 0;
    if (this.y < 0) this.y = this.halfy * 2;
    if (this.y > this.halfy * 2) this.y = 0;
    
    return this;
  }

  getRenderProperties() {
    const position = this.getPosition();
    return {
      ...position,
      size: this.size,
      color: this.color,
      label: this.label,
      kind: this.kind
    };
  }
}

export class Background {
  constructor(width, height, dotCount) {
    this.width = width;
    this.height = height;
    this.halfx = width / 2;
    this.halfy = height / 2;
    // Determine an adaptive particle count if not provided
    if (typeof dotCount === 'number') {
      this.dotCount = dotCount;
    } else {
      const baseArea = 1920 * 1080;
      const area = Math.max(1, width * height);
      const densityScale = area / baseArea;
      const prefersReduced = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
      const target = prefersReduced ? 800 : Math.round(3000 * densityScale);
      this.dotCount = Math.min(Math.max(target, 800), 6000);
    }
    
    this.satelliteNames = [
      '25544-ISS',
      '53323-COSMOS 2558',
      '44421-COSMOS 2535',
      '51445-USA 326',
      '48274-STARLINK-2653',
      '49260-STARLINK-3132',
      '54152-COSMOS 2565',
      '43013-NOAA 20',
      '39634-SENTINEL-1A',
      '40697-SENTINEL-2A',
      '42063-SENTINEL-3A',
      '37849-TERRASAR-X',
      '41456-GOES-16',
      '43226-GOES-17',
      '25994-TERRA',
      '27424-AQUA',
      '38337-SUOMI NPP',
      '28654-AURA',
      '36036-TIANGONG-1',
      '48275-TIANHE'
    ];
    
    const satelliteCount = 20;
    const starCount = Math.max(0, this.dotCount - satelliteCount);
    this.stars = this.createStars(starCount);
    this.satellites = this.createSatellites(satelliteCount);
  }

  // Utility function for crypto random numbers between 0 and 1
  cryptoRandom() {
    return crypto.getRandomValues(new Uint32Array(1))[0] / 4294967295;
  }

  createStars(count) {
    return Array.from({ length: count }, () => {
      return new Dot(this.halfx, this.halfy, {
        y: this.cryptoRandom() * this.height,
        dx: 0.1,
        dy: 0,
        speed: 0.1,
        size: this.cryptoRandom() * 2 + 0.5,
        color: 255,
        kind: 'star'
      });
    });
  }

  createSatellites(count) {
    const usedNames = new Set();
    return Array.from({ length: count }, () => {
      const angle = this.cryptoRandom() * Math.PI;
      
      let label = null;
      if (this.cryptoRandom() > 0.3 && usedNames.size < this.satelliteNames.length) {
        const availableNames = this.satelliteNames.filter(name => !usedNames.has(name));
        if (availableNames.length > 0) {
          const randomIndex = Math.floor(this.cryptoRandom() * availableNames.length);
          label = availableNames[randomIndex];
          usedNames.add(label);
        }
      }
      
      return new Dot(this.halfx, this.halfy, {
        dx: Math.cos(angle),
        dy: Math.sin(angle) * 0.5,
        speed: (2 + this.cryptoRandom()) * 0.75,
        size: 3 + this.cryptoRandom() * 2,
        color: 200 + this.cryptoRandom() * 55,
        label: label,
        kind: 'sat'
      });
    });
  }

  update(dtSeconds = 1 / 60) {
    const allDots = [
      ...this.stars.map(dot => dot.move(dtSeconds)),
      ...this.satellites.map(dot => dot.move(dtSeconds))
    ];
    return allDots.map(dot => dot.getRenderProperties());
  }
}

export class Renderer {
  constructor(canvas, fps = 60) {
    this.canvas = canvas;
    this.context = canvas.getContext('2d', { alpha: false });
    this.lastFrame = 0;
    this.fps = fps;
    this.frameInterval = 1000 / this.fps;
  }

  render(dots, timestamp) {
    if (timestamp - this.lastFrame < this.frameInterval) return;
    this.lastFrame = timestamp;

    // Draw background
    this.context.fillStyle = '#000000';
    this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Draw stars batched (single color and alpha)
    this.context.fillStyle = 'rgba(255,255,255,0.25)';
    this.context.beginPath();
    for (const d of dots) {
      if (d.kind !== 'star') continue;
      this.context.moveTo(d.x + d.size/2, d.y);
      this.context.arc(d.x, d.y, d.size/2, 0, Math.PI * 2);
    }
    this.context.fill();

    // Draw satellites (few in count, per-item color OK)
    for (const d of dots) {
      if (d.kind !== 'sat') continue;
      this.context.fillStyle = `rgba(${d.color},${d.color},${d.color},0.25)`;
      this.context.beginPath();
      this.context.moveTo(d.x + d.size/2, d.y);
      this.context.arc(d.x, d.y, d.size/2, 0, Math.PI * 2);
      this.context.fill();
    }
    
    // Draw labels for satellites
    this.context.font = '12px monospace';
    this.context.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    this.context.lineWidth = 1;

    for (const { x, y, size, label, kind } of dots) {
      if (kind === 'sat' && label) {
        // Calculate label position (offset from dot)
        const labelOffsetX = 30;
        const labelOffsetY = -20;
        const labelX = x + labelOffsetX;
        const labelY = y + labelOffsetY;

        // Draw arrow line from label to dot
        this.context.beginPath();
        this.context.moveTo(labelX - 5, labelY + 5);
        this.context.lineTo(x + size/2, y);
        this.context.stroke();

        // Draw arrowhead pointing to satellite
        const angle = Math.atan2(y - (labelY + 5), (x + size/2) - (labelX - 5));
        const arrowLength = 5;
        const arrowTipX = x + size/2;
        const arrowTipY = y;
        this.context.beginPath();
        this.context.moveTo(arrowTipX, arrowTipY);
        this.context.lineTo(
          arrowTipX - arrowLength * Math.cos(angle - Math.PI/6),
          arrowTipY - arrowLength * Math.sin(angle - Math.PI/6)
        );
        this.context.moveTo(arrowTipX, arrowTipY);
        this.context.lineTo(
          arrowTipX - arrowLength * Math.cos(angle + Math.PI/6),
          arrowTipY - arrowLength * Math.sin(angle + Math.PI/6)
        );
        this.context.stroke();

        // Draw label text
        this.context.fillStyle = 'rgba(255, 255, 255, 0.6)';
        this.context.fillText(label, labelX, labelY);
      }
    }
  }
}

export function render(canvas, options) {
  const fps = options?.fps ?? 60;
  const { background, renderer } = initialize(canvas, fps);
  let isRunning = true;
  let lastUpdate = 0;

  function animate(timestamp) {
    if (!isRunning) return;
    if (lastUpdate === 0) lastUpdate = timestamp;
    const dtSeconds = Math.min(0.05, Math.max(0, (timestamp - lastUpdate) / 1000));
    lastUpdate = timestamp;
    const dots = background.update(dtSeconds);
    renderer.render(dots, timestamp);
    requestAnimationFrame(animate);
  }

  requestAnimationFrame(animate);
  
  return {
    stop: () => {
      isRunning = false;
    },
    background, 
    renderer
  };
}

export function initialize(canvas, fps = 60) {
  if (!canvas) {
    canvas = document.createElement('canvas');
    canvas.id = "starfield";

    canvas.width = document.body.clientWidth;
    canvas.height = document.body.clientHeight;

    document.body.appendChild(canvas);
  }

  const background = new Background(canvas.width, canvas.height);
  const renderer = new Renderer(canvas, fps);

  return { background, renderer };
}